/**
 * Small clock-skew buffer so a token that is about to expire within the next
 * few seconds is treated as already expired, rather than firing a request that
 * is guaranteed to come back as a 401.
 */
const EXPIRY_SKEW_MS = 5000;

/**
 * Decode the `exp` claim (in ms) from a JWT access token.
 * Returns `null` when the token is missing, not a JWT, or has no `exp` claim —
 * in which case callers should fall back to presence-based auth checks rather
 * than assuming the token is expired.
 */
export const getTokenExpiryMs = (token?: string | null): number | null => {
  if (!token) return null;

  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );
    const decoded = JSON.parse(json);

    if (typeof decoded?.exp !== 'number') return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
};

/**
 * Returns `true` only when we can decode the token's `exp` claim AND it is in
 * the past. A missing/undecodable token returns `false` so that existing
 * presence-based behaviour (`!isEmpty(token)`) is preserved and we never log a
 * user out on a token we simply failed to parse.
 */
export const isTokenExpired = (token?: string | null): boolean => {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return false;
  return Date.now() >= expiryMs - EXPIRY_SKEW_MS;
};
